//@flow
import { ProjectActions } from 'modules/project/actions';
import { ProjectSelector } from 'modules/project/selector';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { RouteUtil } from 'utils/RouteUtil';
import { filter, isEmpty } from 'lodash';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { bindActionCreators } from 'redux';
import { RouteNames } from 'router';
import { ExploreButton } from 'components/atoms/exploreButton';
import { Title } from 'components/atoms/title';
import HorizontalCarousel from 'components/molecules/horizontalCarousel';
import { ListMapMobile } from 'components/molecules/listMapMobile';
import MapComponent from 'components/molecules/map';
import { PaginationControl } from 'components/molecules/paginationControl';
import { OverlayModal } from 'components/templates/overlayModal';
import { OverlayPortal } from 'components/templates/overlayPortal';
import { useViewport } from 'components/templates/viewportProvider';
import type { Countries, Project } from 'types/project';
import type { LocationType } from 'types/common';
import './styles.scss';

type Props = {
  data: {
    type: string,
    data: {
      countries: Object<Countries>,
      list: Array<Project>,
    },
    userLocation: Object<LocationType>,
    mapView: boolean,
    updateMapView: () => void,
    product?: Object,
    vertical?: string,
  },
  filteredProjects: Array<Project>,
  activeFilters: Array<string>,
  isFiltered: boolean,
  toggleProjectFilter: (type: string, value: number) => void,
  clearProjectFilters: () => void,
  breadcrumbList: Array<string>,
};

const ProjectBrowser = (props: Props) => {
  const {
    data,
    theme,
    filteredProjects,
    isFiltered,
    activeFilters,
    clearProjectFilters,
    breadcrumbList,
  } = props;
  const type = data.type;
  const projectData = data.data;
  const vertical = data.vertical;
  const projectList = isFiltered ? filteredProjects.list : projectData.list;
  const projectCities = new Set(projectList.map(project => project.cityId));
  const architectOverlayData = data.architectProjectOverlayData
    ? data.architectProjectOverlayData
    : [];
  const awardData = data.awardData;
  const [filteredArchitectData, setFilteredArchitectData] = useState([]);
  const [sliderRef, setSlider] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [showProjects, setShowProjects] = useState(false);
  const [showArchitectOverlay, setShowArchitectOverlay] = useState(false);
  const [projectAwardData, setProjectAwardData] = useState([]);
  const { height, isMobile } = useViewport();

  let isArchitecturePage = props.history.location.pathname.includes(
    RouteNames.architectureDesign
  );

  // clear project filters on unmount
  useEffect(() => {
    return () => {
      clearProjectFilters();
    };
  }, []);

  const goToProjectDetails = list => {
    if (!isArchitecturePage) {
      RouteUtil.setRouteChange(props, `/${list.urlSlug}`, '', '', list.id);
      sessionStorage.setItem('selected_project_id', list.id);
    } else if (type === 'architecture-projects') {
      let overlayData = filter(
        architectOverlayData,
        data => data.project_id === list.id
      );
      setProjectAwardData(
        filter(awardData, data => data.project_id === list.id)
      );
      if (overlayData.length > 0) {
        overlayData[0].title = list.name;
      }
      setFilteredArchitectData(overlayData);
      !isEmpty(overlayData) && setShowArchitectOverlay(true);
    }
  };

  const updateFilter = value => {
    props.toggleProjectFilter('location', value.cityId);
  };

  const handleSliderRef = ref => {
    setSlider(ref);
  };

  const next = () => {
    sliderRef.slickNext();
  };

  const previous = () => {
    sliderRef.slickPrev();
  };

  const showOverlay = showProjects ? (
    <OverlayPortal>
      <OverlayModal
        data={isFiltered ? filteredProjects : projectData}
        theme={isMobile ? 'light-theme' : 'dark-theme'}
        type={type}
        onClosePress={() => setShowProjects(false)}
        activeFilters={activeFilters}
        breadcrumbList={[
          {
            breadcrumbName: 'Homes',
            breadcrumbLink: RouteNames.homes,
          },
          {
            breadcrumbName: 'Project Browser',
            breadcrumbLink: '#',
          },
        ]}
      />
    </OverlayPortal>
  ) : null;

  const renderArchitectOverlay = showArchitectOverlay ? (
    <OverlayPortal>
      <OverlayModal
        data={filteredArchitectData}
        awardData={projectAwardData}
        theme={'light-theme'}
        type={'architect-project'}
        className={'architectOverlay'}
        onClosePress={() => setShowArchitectOverlay(false)}
        breadcrumbList={[
          {
            breadcrumbName: 'Shibanee & Kamal',
            breadcrumbLink: breadcrumbList[1].breadcrumbLink,
          },
          {
            breadcrumbName: breadcrumbList[1].breadcrumbName,
            breadcrumbLink: breadcrumbList[1].breadcrumbLink,
          },
        ]}
      />
    </OverlayPortal>
  ) : null;

  const title = () => {
    const locations = projectCities.size;
    return (
      <Title
        firstText={
          projectList.length === 1
            ? `${projectList.length} property`
            : `${projectList.length} properties`
        }
        secondText={
          locations === 1 ? `${locations} location` : `${locations} locations`
        }
      />
    );
  };

  const renderFooter = () => {
    const totalLength =
      projectList.length && projectList.length > 4 ? projectList.length - 2 : 1;
    return (
      <Row className={'slider-div'}>
        <Col md={{ offset: 9, span: 3 }}>
          <PaginationControl
            className={'section-footer-item'}
            theme={'light-theme'}
            mode={'slider'}
            currentIndex={activeSlide + 1}
            total={totalLength}
            onNextClick={next}
            onPreviousClick={previous}
          />
        </Col>
      </Row>
    );
  };
  return (
    <div id={'projects'}>
      {isMobile ? (
        <ListMapMobile
          updateFilter={updateFilter}
          data={projectData}
          list={projectList}
          type={'project'}
          updateMapView={data.updateMapView}
          fetchTitle={title()}
          onClick={goToProjectDetails}
          mapView={data.mapView}
          pinLocation={data.userLocation}
          activeFilters={activeFilters}
          filterType={type}
          vertical={vertical}
        />
      ) : (
        <div className={'project-browser-container'} style={{ height }}>
          <div className={'project-content'}>
            <div className={'map-container'}>
              <MapComponent
                mapStyles={'project-map'}
                data={projectList}
                // updateFilter={updateFilter}
                type={'project'}
                pinLocation={data.userLocation}
              />
            </div>
            <div className={'list-view-container'}>
              <Row className={'list-header'}>
                <Col md={4} sm={4}>
                  {title()}
                </Col>
                <Col md={8} sm={8}>
                  <ExploreButton
                    onClick={() => setShowProjects(true)}
                    text={'EXPLORE ALL PROJECTS'}
                  />
                </Col>
              </Row>
              <Row className={'list-view-row'}>
                <div className={'list-view'}>
                  {projectList && (
                    <HorizontalCarousel
                      data={{ projectList, vertical }}
                      type={'project'}
                      cssFor={'horizontal-carousel-container'}
                      getActiveSlide={e => {
                        setActiveSlide(e);
                      }}
                      handleSliderRef={handleSliderRef}
                      onClick={goToProjectDetails}
                    />
                  )}
                </div>
              </Row>
            </div>
          </div>
          {projectList.length > 3 && (
            <div className={'project-footer'}>{renderFooter()}</div>
          )}
        </div>
      )}
      {showOverlay}
      {renderArchitectOverlay}
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const { product } = props.data;
  return {
    filteredProjects: ProjectSelector.getFilteredProjects(
      state,
      product ? product.id : null
    ),
    activeFilters: ProjectSelector.getActiveProjectFilterNames(state),
    isFiltered: ProjectSelector.isProjectsFiltered(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { toggleProjectFilter, clearProjectFilters } = ProjectActions;
  return bindActionCreators(
    {
      toggleProjectFilter,
      clearProjectFilters,
    },
    dispatch
  );
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectBrowser);

export default withRouter(connectedComponent);
